import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import Footer from "../components/footer";
import Header from "../components/header";
import Attachments from "../components/attachments";
import SurveyError from "./survey-error";
import AppsLogin from "./apps-login";
import Translate, { TranslateProvider } from "../components/translate";
import TranslationButton from "../components/translation-button";
import Form from "./form";
import DeleteSubmissionModal from "../components/delete-submission-modal";
import ForgotPassword from "./forgot-password";
import CmsPage from "../components/cms-page";
import CookiePolicy from "./cookie-policy";
import Title from "../components/title";
import UserContext from "../context/user";
import LanguageContext from "../context/language";
import { parseSubmission } from "../utils/partial";
import { translateSurvey } from "../utils/lang";
import useTealiumOnPageLoad from "../utils/tealium";
import useAppLanguage from "../hooks/useAppLanguage";
import CookieBanner from "../components/cookie-banner";
import useDocumentTitle from "../hooks/useDocumentTitle";

const Survey = () => {
  const { logged, callApi } = useContext(UserContext);
  const { language, defaultLabels, unloggedLanguage } = useContext(LanguageContext);
  const [surveyStatus, setSurveyStatus] = useState("OK"); //OK, NOT_AVAILABLE, 403, COMPLETED
  const [path, setPath] = useState();
  const [partial, setPartial] = useState(false);
  const [lastSubmission, setLastSubmission] = useState();
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [formData, setFormData] = useState({});
  const { id } = useParams();
  const [forgotPw, setForgotPw] = useState();
  const [surveyNavigation, setSurveyNavigation] = useState();
  useTealiumOnPageLoad({ pageSection1: "survey" });
  useDocumentTitle("EssilorLuxottica Survey");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const {
    isFetching,
    error,
    data: survey,
  } = useQuery(["survey", id], () => callApi(`/survey/${id}`), {
    enabled: logged,
    select: ({ definition, ...data }) => {
      return {
        ...data,
        definition: {
          ...definition,
          sections: definition.sections.sort((a, b) =>
            a.position > b.position ? 1 : a.position < b.position ? -1 : 0
          ),
        },
      };
    },
    onSuccess: (data) => {
      const sections = data.definition.sections;
      setSurveyStatus(
        data.definition.submissionsEnabled &&
          (data.definition.submissionLimitDate
            ? dayjs().isBefore(dayjs(Number(data.definition.submissionLimitDate)))
            : true)
          ? "OK"
          : "NOT_AVAILABLE"
      );
      setPath([sections[0].id]);
    },
  });

  const { appLang, setAppLang, languages, labels } = useAppLanguage(
    defaultLabels,
    !isFetching && language,
    survey,
    logged ? null : unloggedLanguage
  );

  /*  const {
    //isLoading,
    //error: answersError,
    //data: submissions,
  } = */ useQuery(
    ["answers", id],
    () => callApi(`/survey/${id}/${survey?.definition?.id}/answers/mine`),
    {
      enabled: logged && !!survey?.definition?.id,
      select: (data) => data.list,
      onSuccess: (data) => {
        const submission = data.find((sub) => sub.status === "SUBMITTED");
        if (submission && !survey.definition.multipleSubmissionEnabled) {
          setFormData(parseSubmission(survey, submission));
          setReadOnlyMode(!isEditable);
          setLastSubmission(submission);
          setPartial(submission.submissionId);
        }
        const draft = data
          ?.sort((a, b) =>
            a.creationTimeStamp > b.creationTimeStamp
              ? 1
              : a.creationTimeStamp < b.creationTimeStamp
              ? -1
              : 0
          )
          .find((sub) => sub.status === "DRAFT");
        if (draft) {
          setPartial(draft.submissionId);
          setFormData(parseSubmission(survey, draft));
        }
      },
    }
  );

  const translatedSurvey = useMemo(() => {
    if (survey) {
      return translateSurvey(survey, appLang);
    }
  }, [survey, appLang]);

  const section = useMemo(() => {
    if (path) {
      const current = path.slice(-1)[0];
      return translatedSurvey?.definition?.sections?.find((section) => section.id === current);
    }
  }, [path, translatedSurvey]);

  const currentSectionIndex = useMemo(
    () =>
      translatedSurvey?.definition?.sections?.findIndex(
        (section) => section.id === path?.slice(-1)[0]
      ),
    [path, translatedSurvey]
  );

  const isEditable = useMemo(
    () =>
      translatedSurvey?.definition?.editSubmissionEnabled &&
      (translatedSurvey?.definition?.editAndRemoveLimitDate
        ? dayjs().isBefore(dayjs(Number(translatedSurvey?.definition?.editAndRemoveLimitDate)))
        : true),
    [translatedSurvey]
  );

  /* Form handling functions*/

  const handlePrevious = () => setPath((current) => current.slice(0, current.length - 1));

  const handleNext = (submissionId, nextIsGoToSection) => {
    if (submissionId) {
      setPartial(submissionId);
    }
    setPath((current) =>
      current.concat(
        nextIsGoToSection ?? translatedSurvey?.definition?.sections[currentSectionIndex + 1]?.id
      )
    );
  };

  const handleSuccessSubmit = () => setSurveyStatus("COMPLETED");

  const enterReviewMode = () => {
    setReadOnlyMode(!isEditable);
    setPath([survey.definition.sections[0].id]);
    setSurveyStatus("OK");
  };

  const handleDeleteSubmission = useCallback(() => {
    setReadOnlyMode(false);
    setPath([survey.definition.sections[0].id]);
    setSurveyStatus("OK");
    setFormData({});
    setPartial();
  }, [survey]);

  const handleNewSubmission = useCallback(() => {
    setPath([survey.definition.sections[0].id]);
    setSurveyStatus("OK");
    setFormData({});
    setPartial();
  }, [survey]);

  const handleCloseModal = () => setDeleteModal(false);

  return (
    <>
      {logged ? (
        isFetching ? (
          <div className="loading-spinner mt" />
        ) : (
          <>
            <Header
              selectedLanguage={appLang}
              setSelectedLanguage={setAppLang}
              languages={languages}
              labels={labels}
            />
            <TranslateProvider
              id={`${surveyNavigation ? surveyNavigation + "/" : ""}${currentSectionIndex ?? ""}${
                appLang ? "-" + appLang.value : ""
              }`}
            >
              <main className="survey-detail">
                {error ? (
                  <SurveyError status={error?.status} labels={labels} />
                ) : surveyNavigation ? (
                  {
                    "privacy-policy": (
                      <CmsPage
                        path="footer/privacy-policy"
                        title={labels?.WELFARE_PRIVACY_POLICY}
                        setNavigation={setSurveyNavigation}
                        key={surveyNavigation}
                        appLang={appLang}
                      />
                    ),
                    "cookie-policy": (
                      <CookiePolicy
                        logged={true}
                        setNavigation={setSurveyNavigation}
                        appLang={appLang}
                      />
                    ),
                  }[surveyNavigation]
                ) : (
                  <>
                    {["OK", "COMPLETED"].includes(surveyStatus) && (
                      <Title
                        showStepper={
                          surveyStatus === "OK" &&
                          translatedSurvey?.definition?.sections?.length > 1
                        }
                        stepper={{
                          sections: translatedSurvey?.definition?.sections,
                          currentSectionIndex: currentSectionIndex,
                        }}
                      >
                        <Translate text={translatedSurvey?.title} />
                      </Title>
                    )}

                    <div
                      className={classNames(
                        "wrapper wrapper-small page-content",
                        !["OK", "COMPLETED"].includes(surveyStatus) && "page-content--error"
                      )}
                    >
                      {surveyStatus === "OK" &&
                        path?.length === 1 &&
                        translatedSurvey?.coverUrl && (
                          <div className="survey-detail__cover-wrapper">
                            <img
                              className="survey-detail__cover"
                              src={translatedSurvey.coverUrl}
                              alt="Survey cover"
                            />
                          </div>
                        )}
                      {surveyStatus === "OK" ? (
                        section && (
                          <>
                            <div className="app__content">
                              {translatedSurvey?.description && path.length === 1 && (
                                <Translate
                                  className="survey-detail__description"
                                  html={translatedSurvey.description}
                                />
                              )}
                              <div className="survey-detail__section" key={section.id}>
                                {translatedSurvey?.definition?.sections.length > 1 && (
                                  <div className="survey-detail__step-title">
                                    {labels?.WELFARE_STEP || "Step"} {currentSectionIndex + 1}
                                  </div>
                                )}
                                {section?.label && (
                                  <h2 className="survey-detail__section-title">
                                    <Translate text={section.label} />
                                  </h2>
                                )}
                                {section.description && (
                                  <p className="survey-detail__section-description">
                                    <Translate text={section.description} />
                                  </p>
                                )}
                                {labels?._LANGUAGE === appLang?.value ? (
                                  <Form
                                    key={section.id}
                                    section={section}
                                    survey={translatedSurvey}
                                    partial={partial}
                                    currentSectionIndex={currentSectionIndex}
                                    handlePrevious={handlePrevious}
                                    handleNext={handleNext}
                                    handleSuccessSubmit={handleSuccessSubmit}
                                    formData={formData}
                                    setFormData={setFormData}
                                    readOnlyMode={readOnlyMode}
                                    labels={labels}
                                    locale={appLang?.value}
                                  />
                                ) : (
                                  <div className="loading-spinner" />
                                )}
                              </div>
                              {translatedSurvey?.attachments.length > 0 && path.length === 1 && (
                                <Attachments
                                  attachments={translatedSurvey.attachments}
                                  labels={labels}
                                />
                              )}
                            </div>
                          </>
                        )
                      ) : surveyStatus === "COMPLETED" ? (
                        <div className="app__content app__content--feedback">
                          <h2>
                            {labels?.WELFARE_FEEDBACK_MESSAGE || "Thank you for sharing your voice"}
                          </h2>
                          {translatedSurvey?.definition?.multipleSubmissionEnabled ? (
                            <button onClick={handleNewSubmission} className="button primary">
                              {labels?.WELFARE_ADD_NEW_SUBMISSION || "Add new submission"}
                            </button>
                          ) : (
                            <>
                              <button onClick={enterReviewMode} className="link link--text">
                                {isEditable
                                  ? labels?.WELFARE_REVIEW_YOUR_ANSWERS || "Review your answers"
                                  : labels?.WELFARE_VIEW_YOUR_ANSWERS || "View your answers"}
                              </button>
                              {survey?.definition?.deleteSubmissionEnabled && lastSubmission && (
                                <button
                                  onClick={() => setDeleteModal(true)}
                                  className="link link--text"
                                >
                                  {labels?.WELFARE_DELETE_YOUR_SUBMISSION ||
                                    "Delete your submission"}
                                </button>
                              )}
                            </>
                          )}
                          <DeleteSubmissionModal
                            show={deleteModal}
                            closeModal={handleCloseModal}
                            onDeleteSubmission={handleDeleteSubmission}
                            surveyId={id}
                            id={lastSubmission?.submissionId}
                            key={lastSubmission?.submissionId}
                            labels={labels}
                          />
                        </div>
                      ) : (
                        <SurveyError status={surveyStatus} labels={labels} />
                      )}
                    </div>
                  </>
                )}
                {labels?._LANGUAGE === appLang?.value && <TranslationButton labels={labels} />}
              </main>
            </TranslateProvider>
            <Footer setNavigation={setSurveyNavigation} labels={labels} />
          </>
        )
      ) : forgotPw ? (
        <ForgotPassword setForgotPw={setForgotPw} labels={labels} />
      ) : surveyNavigation === "cookie-policy" ? (
        <CookiePolicy logged={false} setNavigation={setSurveyNavigation} appLang={appLang} />
      ) : (
        <AppsLogin setForgotPw={setForgotPw} labels={labels} />
      )}
      <CookieBanner labels={labels} setNavigation={setSurveyNavigation} />
    </>
  );
};

export default Survey;
